import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import InstallationLicenseBar from "@/components/InstallationLicenseBar";
import {
  PLATFORM_DESIGNER_BUYER_PREVIEW_NO_ORG_BODY,
  PLATFORM_DESIGNER_BUYER_PREVIEW_NO_ORG_TITLE,
  PLATFORM_DESIGNER_BUYER_PREVIEW_SIMULATED_NOTE,
} from "@/lib/platformDesignerBuyerPreview";
import { usePlatformDesignerBuyerPreviewOrg } from "@/lib/usePlatformDesignerBuyerPreviewOrg";

/**
 * Cabecera compacta Panel General — preview comprador (selector org + barra licencia).
 * El panel «Licencia y ampliaciones» vive en el cuerpo de PanelGeneral.
 */
export default function PlatformDesignerBuyerPreviewHeader({ baseSystemInfo = null }) {
  const {
    organizations,
    loading,
    orgId,
    setOrgId,
    hasOrganizationSelected,
    previewSystemInfo,
  } = usePlatformDesignerBuyerPreviewOrg({ baseSystemInfo });

  return (
    <div className="space-y-2 w-full min-w-0" data-testid="platform-designer-buyer-preview-header">
      <div className="rounded-sm border border-sky-200 bg-sky-50/90 px-3 py-2 space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-sky-950 m-0 leading-tight">
              {PLATFORM_DESIGNER_BUYER_PREVIEW_NO_ORG_TITLE}
            </h2>
            <p className="text-[11px] text-sky-900/90 mt-0.5 mb-0 leading-snug">
              {hasOrganizationSelected
                ? "Datos reales de la organización seleccionada."
                : PLATFORM_DESIGNER_BUYER_PREVIEW_NO_ORG_BODY}
            </p>
          </div>
          <div className="flex flex-col gap-1 sm:w-[min(100%,20rem)] shrink-0">
            <label
              htmlFor="buyer-preview-org-select"
              className="text-[10px] font-semibold uppercase tracking-wider text-sky-900"
            >
              Organización
            </label>
            {loading ? (
              <span className="inline-flex items-center gap-2 text-xs text-sky-800 h-8">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Cargando…
              </span>
            ) : (
              <Select
                value={orgId || "__none__"}
                onValueChange={(v) => setOrgId(v === "__none__" ? "" : v)}
              >
                <SelectTrigger
                  id="buyer-preview-org-select"
                  className="h-8 text-xs bg-white"
                  data-testid="buyer-preview-org-select"
                >
                  <SelectValue placeholder="Seleccionar organización…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin organización (simulación) —</SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={String(org.id)}>
                      {(org.name || "Organización").trim()}
                      {org.rfc ? ` · ${org.rfc}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        {!hasOrganizationSelected ? (
          <p className="text-[10px] text-sky-800/90 m-0 leading-snug" data-testid="buyer-preview-simulation-note">
            {PLATFORM_DESIGNER_BUYER_PREVIEW_SIMULATED_NOTE}
          </p>
        ) : null}
      </div>

      {hasOrganizationSelected && previewSystemInfo ? (
        <InstallationLicenseBar info={previewSystemInfo} canRequestLicense={false} showRenewalExpansionCta />
      ) : (
        <p
          className="text-[10px] text-slate-600 m-0 px-0.5 leading-snug"
          data-testid="buyer-preview-demo-modules-hint"
        >
          Modo demo: seleccione una organización para ver licencia, titular, RFC y módulos autorizados.
        </p>
      )}
    </div>
  );
}
